/**
 * Declarative, history-derived flow state machine.
 *
 * A flow is a named map of states; each state carries an `enter` transform (or
 * list) applied when the flow is resting in that state, and a `transition`
 * describing where to go next. The CURRENT state is DERIVED purely from the
 * transcript — there is no stored cursor. Derivation walks the transition graph
 * from the entry state, advancing past any state whose work the transcript shows
 * is already done, and stops at the first unsatisfied state (or a terminal).
 */

import type { Continuation, FlowOutput, FlowResolverEntry, ReadonlyTranscript, TurnTransform } from './types.ts';

/** A predicate over the transcript used by branch/loop transitions. */
export type FlowPredicate = (transcript: ReadonlyTranscript) => boolean;

/** Move unconditionally to another state. */
export interface NextTransition {
  kind: 'next';
  to: string;
}

/** Move to one of two states depending on a transcript predicate. */
export interface BranchTransition {
  kind: 'branch';
  predicate: FlowPredicate;
  ifTrue: string;
  ifFalse: string;
}

/** Loop back to the enclosing state while `backWhen` holds, else exit. */
export interface LoopTransition {
  kind: 'loop';
  backWhen: FlowPredicate;
  exit: string;
  maxIterations?: number;
}

/** Stop the agent loop — the flow has reached an accepting end. */
export interface TerminalTransition {
  kind: 'terminal';
}

export type FlowTransition = NextTransition | BranchTransition | LoopTransition | TerminalTransition;

/** One state of a flow: what to do on entry and where to go next. */
export interface FlowState {
  /** The wire name of the tool whose presence in the transcript marks this
   * state's work as done. When absent, the state advances on its transition
   * the first time it is reached (a pass-through / decision state). */
  tool?: string;
  enter: TurnTransform | TurnTransform[];
  transition: FlowTransition;
}

/** Move unconditionally to another state. */
export function next(to: string): NextTransition {
  return { kind: 'next', to };
}

/** Move to one of two states depending on a transcript predicate. */
export function branch(predicate: FlowPredicate, ifTrue: string, ifFalse: string): BranchTransition {
  return { kind: 'branch', predicate, ifTrue, ifFalse };
}

/** Loop back while `backWhen` holds, exiting to `exit` once it stops (or the
 * iteration cap is hit). */
export function loop(backWhen: FlowPredicate, exit: string, opts: { maxIterations?: number } = {}): LoopTransition {
  return { kind: 'loop', backWhen, exit, maxIterations: opts.maxIterations };
}

/** Terminal state — sets continuation `'stop'`. */
export function terminal(): TerminalTransition {
  return { kind: 'terminal' };
}

/** Hard cap on derivation steps — guarantees totality even for cyclic graphs. */
const MAX_DERIVATION_STEPS = 1024;

/**
 * Resolve the destination state id for a transition, given the transcript and
 * the id of the state owning the transition. `null` means "rest here" — the
 * flow stays in the owning state. Looked up by `kind` rather than branched.
 */
const TRANSITION_TARGETS: {
  [KindType in FlowTransition['kind']]: (
    transition: Extract<FlowTransition, { kind: KindType }>,
    ctx: { transcript: ReadonlyTranscript; stateId: string; state: FlowState },
  ) => string | null;
} = {
  next: (transition) => transition.to,
  branch: (transition, { transcript }) => (transition.predicate(transcript) ? transition.ifTrue : transition.ifFalse),
  loop: (transition, { transcript, state }) => {
    const iterations = state.tool ? transcript.toolCallCount(state.tool) : 0;
    const capped = transition.maxIterations !== undefined && iterations >= transition.maxIterations;
    if (!capped && transition.backWhen(transcript)) {
      return null;
    }
    return transition.exit;
  },
  terminal: () => null,
};

/** Whether a state's work is recorded as done in the transcript. A state with
 * no `tool` is a pass-through and is always considered done so its transition
 * is followed immediately. */
function isStateSatisfied(state: FlowState, transcript: ReadonlyTranscript): boolean {
  if (!state.tool) {
    return true;
  }
  return transcript.toolCallCount(state.tool) > 0;
}

/** Normalise a state's `enter` to an array of transforms. */
function enterTransforms(state: FlowState): TurnTransform[] {
  return Array.isArray(state.enter) ? state.enter : [state.enter];
}

/**
 * Derive the resting state id purely from the transcript by folding the
 * transition graph from `entryId`. Advances past satisfied states; stops at the
 * first unsatisfied state, a terminal, or a transition that elects to rest.
 */
function deriveStateId(states: Record<string, FlowState>, entryId: string, transcript: ReadonlyTranscript): string {
  const step = (stateId: string, remaining: number): string => {
    const state = states[stateId];
    if (remaining <= 0 || !state || state.transition.kind === 'terminal' || !isStateSatisfied(state, transcript)) {
      return stateId;
    }
    const resolve = TRANSITION_TARGETS[state.transition.kind] as (
      transition: FlowTransition,
      ctx: { transcript: ReadonlyTranscript; stateId: string; state: FlowState },
    ) => string | null;
    const target = resolve(state.transition, { transcript, stateId, state });
    if (target === null) {
      return stateId;
    }
    return step(target, remaining - 1);
  };
  return step(entryId, MAX_DERIVATION_STEPS);
}

/**
 * Build a {@link FlowResolverEntry} from a declarative state map. The first
 * key in `states` is the entry state. On each turn the resolver derives the
 * resting state, emits that state's `enter` transforms, and — once a terminal
 * state's work has settled — votes `'stop'`.
 */
export function flow(_name: string, states: Record<string, FlowState>, priority = 100): FlowResolverEntry {
  const entryId = Object.keys(states)[0];
  return {
    priority,
    resolve({ transcript }): FlowOutput {
      if (entryId === undefined) {
        return { transforms: [] };
      }
      const stateId = deriveStateId(states, entryId, transcript);
      const state = states[stateId];
      if (!state) {
        return { transforms: [] };
      }
      const continuation: Continuation | undefined =
        state.transition.kind === 'terminal' && isStateSatisfied(state, transcript) ? 'stop' : undefined;
      return { transforms: enterTransforms(state), continuation };
    },
  };
}
